import { Form } from "../../core/Form";
import { EmailVerificationModal } from "../../components/EmailVerificationModal";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^0\d{1,2}-?\d{3,4}-?\d{4}$/;
const WAY_SECTION_SELECTOR = ".fh-way";

const DISTRICT_OPTIONS: Record<string, string[]> = {
    서울: ["강남구","강동구","강북구","강서구","관악구","광진구","구로구","금천구","노원구","도봉구","동대문구","동작구","마포구","서대문구","서초구","성동구","성북구","송파구","양천구","영등포구","용산구","은평구","종로구","중구","중랑구"],
    경기: ["수원시","성남시","의정부시","안양시","부천시","광명시","평택시","동두천시","안산시","고양시","과천시","구리시","남양주시","오산시","시흥시","군포시","의왕시","하남시","용인시","파주시","이천시","안성시","김포시","화성시","광주시","양주시","포천시","여주시","연천군","가평군","양평군"],
    인천: ["중구","동구","미추홀구","연수구","남동구","부평구","계양구","서구","강화군","옹진군"],
    부산: ["중구","서구","동구","영도구","부산진구","동래구","남구","북구","해운대구","사하구","금정구","강서구","연제구","수영구","사상구","기장군"],
    대구: ["중구","동구","서구","남구","북구","수성구","달서구","달성군","군위군"],
    광주: ["동구","서구","남구","북구","광산구"],
    대전: ["동구","중구","서구","유성구","대덕구"],
    울산: ["중구","남구","동구","북구","울주군"],
    세종: ["세종특별자치시"],
    강원: ["춘천시","원주시","강릉시","동해시","태백시","속초시","삼척시","홍천군","횡성군","영월군","평창군","정선군","철원군","화천군","양구군","인제군","고성군","양양군"],
    충북: ["청주시","충주시","제천시","보은군","옥천군","영동군","증평군","진천군","괴산군","음성군","단양군"],
    충남: ["천안시","공주시","보령시","아산시","서산시","논산시","계룡시","당진시","금산군","부여군","서천군","청양군","홍성군","예산군","태안군"],
    전북: ["전주시","군산시","익산시","정읍시","남원시","김제시","완주군","진안군","무주군","장수군","임실군","순창군","고창군","부안군"],
    전남: ["목포시","여수시","순천시","나주시","광양시","담양군","곡성군","구례군","고흥군","보성군","화순군","장흥군","강진군","해남군","영암군","무안군","함평군","영광군","장성군","완도군","진도군","신안군"],
    경북: ["포항시","경주시","김천시","안동시","구미시","영주시","영천시","상주시","문경시","경산시","의성군","청송군","영양군","영덕군","청도군","고령군","성주군","칠곡군","예천군","봉화군","울진군","울릉군"],
    경남: ["창원시","진주시","통영시","사천시","김해시","밀양시","거제시","양산시","의령군","함안군","창녕군","고성군","남해군","하동군","산청군","함양군","거창군","합천군"],
    제주: ["제주시","서귀포시"],
};

function showFormSuccess(form: HTMLFormElement, message: string): void {
    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!submitBtn) return;
    form.querySelector("[data-submit-success]")?.remove();
    const msg = document.createElement("p");
    msg.setAttribute("data-submit-success", "");
    msg.className = "form__success-msg";
    msg.textContent = message;
    submitBtn.insertAdjacentElement("beforebegin", msg);
    submitBtn.textContent = "재전송";
}

function pickFirstErrorMessage(data: Record<string, unknown> | undefined, fallback: string): string {
    if (!data) return fallback;
    const message = typeof data.message === "string" ? data.message : "";
    const errors = data.errors as Record<string, string[]> | null | undefined;
    if (errors && typeof errors === "object") {
        for (const key of Object.keys(errors)) {
            const arr = errors[key];
            if (Array.isArray(arr) && arr.length > 0) {
                return message || arr[0];
            }
        }
    }
    return message || fallback;
}

function initWayPieAnimation(): void {
    const section = document.querySelector<HTMLElement>(WAY_SECTION_SELECTOR);
    if (!section || !section.querySelector(".pie")) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    section.classList.add("is-pie-ready");

    const reveal = (): void => {
        section.classList.add("is-pie-visible");
    };

    if (!("IntersectionObserver" in window)) {
        reveal();
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            reveal();
            observer.disconnect();
        },
        {
            threshold: 0.28,
            rootMargin: "0px 0px -12% 0px",
        },
    );

    observer.observe(section);
}

export const FranchiseController = {
    index(): void {
        initWayPieAnimation();

        const form = document.querySelector<HTMLFormElement>("[data-franchise-form]");
        if (!form) return;

        // 지역→시군구 동기화
        const regionEl = form.elements.namedItem("region") as HTMLSelectElement | null;
        const districtEl = form.elements.namedItem("district") as HTMLSelectElement | null;
        const syncDistricts = (): void => {
            if (!regionEl || !districtEl) return;
            const list = DISTRICT_OPTIONS[regionEl.value] ?? [];
            districtEl.innerHTML = '<option value="">시/군/구</option>';
            for (const d of list) {
                const opt = document.createElement("option");
                opt.value = d;
                opt.textContent = d;
                districtEl.appendChild(opt);
            }
        };
        regionEl?.addEventListener("change", syncDistricts);
        syncDistricts();

        const modal = new EmailVerificationModal({
            form,
            emailInputSelector: "#franchise_email",
            verifiedHiddenSelector: "[data-email-verified]",
            verifiedLabelSelector: "[data-email-verified-label]",
            openButtonSelector: "[data-email-verify-open]",
            purpose: "franchise",
        });
        modal.init();

        new Form(form, {
            submitButtonSelector: 'button[type="submit"]',
            loaderSelector: "[data-form-loader]",
            submittingText: "전송 중...",
            validate: (f) => {
                const getVal = (name: string): string => {
                    const el = f.elements.namedItem(name) as
                        | HTMLInputElement
                        | HTMLSelectElement
                        | HTMLTextAreaElement
                        | RadioNodeList
                        | null;
                    if (!el) return "";
                    if (el instanceof RadioNodeList) return el.value;
                    return (el.value ?? "").toString();
                };

                const required: Array<[string, string]> = [
                    ["name", "이름을 입력해 주세요."],
                    ["email", "이메일을 입력해 주세요."],
                    ["phone", "연락처를 입력해 주세요."],
                    ["startup_budget", "창업 비용을 입력해 주세요."],
                    ["region", "지역을 선택해 주세요."],
                    ["district", "시/군/구를 선택해 주세요."],
                    ["channel", "유입경로를 선택해 주세요."],
                    ["contents", "문의 내용을 입력해 주세요."],
                ];
                for (const [name, msg] of required) {
                    if (getVal(name).trim() === "") {
                        const el = f.elements.namedItem(name) as HTMLElement | null;
                        el?.focus?.();
                        return msg;
                    }
                }

                if (!EMAIL_PATTERN.test(getVal("email").trim())) {
                    (f.elements.namedItem("email") as HTMLElement | null)?.focus?.();
                    return "정상적인 이메일을 입력해 주세요.";
                }
                if (!modal.isVerified()) {
                    (f.querySelector<HTMLButtonElement>("[data-email-verify-open]"))?.click();
                    return "이메일 인증을 완료해 주세요.";
                }
                if (!PHONE_PATTERN.test(getVal("phone").trim())) {
                    (f.elements.namedItem("phone") as HTMLElement | null)?.focus?.();
                    return "연락처를 올바른 형식으로 입력해 주세요.";
                }
                if (!f.querySelector('input[name="store_status"]:checked')) {
                    (f.querySelector<HTMLElement>('input[name="store_status"]'))?.focus();
                    return "점포 유무를 선택해 주세요.";
                }
                const agree = f.elements.namedItem("privacy_agree") as HTMLInputElement | null;
                if (!agree || !agree.checked) {
                    agree?.focus?.();
                    return "개인정보수집약관에 동의해 주세요.";
                }
                return null;
            },
            onSuccess: (data) => {
                const msg = (data?.message as string) ?? "가맹 문의가 접수되었습니다.";
                showFormSuccess(form, msg);
            },
            onFail: (message, data) => {
                window.alert(pickFirstErrorMessage(data, message || "접수 중 오류가 발생했습니다."));
            },
            requestFailedMessage: "네트워크 오류로 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        }).init();
    },
    apply(): void {},
};
